// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused".
// The hub import warmed the Rematch model cluster; this specifier restores
// that init order. It retires with the Z2 codemod.
import '@/pages/api/scribeAPIV2';

import { menuCoachmarkStepAtom } from '@/atoms/menuCoachmarkAtoms';
import {
  sidebarPanelContentAtom,
  sidebarPanelPinnedAtom,
} from '@/atoms/sidebarPanelAtoms';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightProgress,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useMutateMarkModalAsViewed } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { sharedMessages } from './coachmark.messages';
import { SPOTLIGHT_FADE_OUT_MS, TOTAL_STEPS } from './constants';

const messages = defineMessages({
  title: {
    defaultMessage: 'Hide sessions to focus',
    id: '2sZweE5LRB',
    description: 'Coachmark title for hiding the session panel',
  },
  body: {
    defaultMessage: 'Use this to hide your sessions when you want more space',
    id: 'JKKbo029FP',
    description: 'Coachmark body for hiding the session panel',
  },
});

interface MenuCoachmarkPinStepProps {
  children?: ReactNode;
}

/**
 * Step 1/3 of the menu coachmark tour.
 * Self-contained: reads atom state directly, provides its own handlers.
 * Shows the pin button and explains how to hide sessions.
 *
 * When "Next" is clicked, the sequence is:
 * 1. Spotlight fades out (Radix exit animation)
 * 2. Panel unpins and step atom advances to 2 (in the same callback)
 * 3. HoverStep waits for the panel collapse before becoming visible
 *
 * Figma: node 1345:78120
 */
export const MenuCoachmarkPinStep = ({ children }: MenuCoachmarkPinStepProps) => {
  const currentStep = useAtomValue(menuCoachmarkStepAtom);
  const isPinned = useAtomValue(sidebarPanelPinnedAtom);
  const setStep = useSetAtom(menuCoachmarkStepAtom);
  const setIsPinned = useSetAtom(sidebarPanelPinnedAtom);
  const setHoverContent = useSetAtom(sidebarPanelContentAtom);
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();

  const isStep1 = currentStep === 1;

  // Local open state so we can trigger the Radix exit animation
  // independently of the atom, keeping the component mounted during fade-out.
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [shouldMount, setShouldMount] = useState(false);
  const transitionTimersRef = useRef<number[]>([]);

  // Sync local state when step becomes active
  useEffect(() => {
    if (isStep1) {
      setShouldMount(true);
      setSpotlightOpen(true);
    }
  }, [isStep1]);

  // Auto-advance: if the user manually unpins while the spotlight is still
  // open, treat it as completing this step and advance to step 2.
  // Guard: handleNext sets spotlightOpen=false BEFORE setIsPinned(false),
  // so this effect only fires for manual-unpin (spotlightOpen is still true).
  useEffect(() => {
    if (isStep1 && spotlightOpen && !isPinned) {
      setSpotlightOpen(false);

      const t1 = window.setTimeout(() => {
        setShouldMount(false);
        setHoverContent(null);
        setStep(2);
      }, SPOTLIGHT_FADE_OUT_MS);
      transitionTimersRef.current.push(t1);
    }
  }, [isStep1, spotlightOpen, isPinned, setStep, setHoverContent]);

  // Clean up timers on unmount
  useEffect(() => {
    const timers = transitionTimersRef.current;
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  const handleNext = useCallback(() => {
    // 1. Fade out the spotlight (Radix exit animation)
    setSpotlightOpen(false);

    // 2. After fade-out completes, unpin panel and advance to step 2.
    //    Both happen in the same callback to avoid a second timer that can
    //    be cleared if the component remounts (e.g., /scribe redirect).
    //    Step 2's visibility delay accounts for the panel collapse animation.
    const t1 = window.setTimeout(() => {
      setShouldMount(false);
      setIsPinned(false);
      setHoverContent(null);
      setStep(2);
    }, SPOTLIGHT_FADE_OUT_MS);
    transitionTimersRef.current.push(t1);
  }, [setStep, setIsPinned, setHoverContent]);

  const handleDismiss = useCallback(() => {
    markModalAsViewed({
      modal_id: ModalId.MENU_COACHMARK_TOUR,
      increment_count: true,
    });
  }, [markModalAsViewed]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setSpotlightOpen(false);
        setShouldMount(false);
        setStep(null);
      }
    },
    [setStep]
  );

  if (!shouldMount) return <>{children}</>;

  return (
    <Spotlight open={spotlightOpen} onOpenChange={handleOpenChange}>
      {children && <SpotlightAnchor>{children}</SpotlightAnchor>}
      <SpotlightContent
        align="start"
        alignOffset={0}
        side="bottom"
        sideOffset={8}
        data-testid="menu-coachmark-step-1"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader>
          <SpotlightTitle>
            <FormattedMessage {...messages.title} />
          </SpotlightTitle>
          <SpotlightClose
            onClick={handleDismiss}
            data-testid="menu-coachmark-step-1-close"
          />
        </SpotlightHeader>
        <SpotlightDescription>
          <FormattedMessage {...messages.body} />
        </SpotlightDescription>
        <SpotlightFooter>
          <SpotlightProgress>
            <FormattedMessage
              {...sharedMessages.stepProgress}
              values={{ current: 1, total: TOTAL_STEPS }}
            />
          </SpotlightProgress>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleNext}
            className="bg-surface text-text-primary hover:bg-background-secondary"
            data-testid="menu-coachmark-step-1-next"
          >
            <FormattedMessage {...sharedMessages.next} />
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
